import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  Chip,
  Link,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  ArrowBack,
  Refresh,
  Edit,
  Block,
  PlayArrow,
  AccountBalanceWallet,
  ReceiptLong,
  Handshake,
  LockReset,
  ContentCopy,
  CheckCircle,
  DeleteForever
} from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import {
  ResellerDetail,
  ResellerMonthlyStatement,
  WholesaleEntry,
  WholesaleTopUpRequest
} from '../types/reseller';
import { formatCredits } from '../utils/credits';
import LoadingButton from '../components/LoadingButton';
import PortalAccessPanel from '../components/PortalAccessPanel';
import { formatPct } from './PartnersPage';

// Detalle ADMIN de un partner: condiciones comerciales (cupos/descuento),
// recarga de la bolsa mayorista, ledger, suspensión y statement mensual.
// Mismo patrón que ClientAccountDetailPage.

const PartnerDetailAdminPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const resellerId = Number(id);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [partner, setPartner] = useState<ResellerDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Editar condiciones comerciales — PATCH parcial
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDiscount, setEditDiscount] = useState('0');
  const [editMaxClients, setEditMaxClients] = useState('1');
  const [editMaxWholesale, setEditMaxWholesale] = useState('0');
  // Cupo AGREGADO de empleados IA de toda su cartera (0 = sin límite).
  const [editMaxEmployees, setEditMaxEmployees] = useState('0');
  const [editMaxStorage, setEditMaxStorage] = useState('0');
  // GiB que el marketplace le REGALA sobre el total de su cartera: el exceso se
  // le cobra a él por ciclo (a sus clientes no se les cobra el disco).
  const [editIncludedStorage, setEditIncludedStorage] = useState('20');
  const [editNotes, setEditNotes] = useState('');

  // Recarga mayorista (purchase) o corrección (adjustment, con signo)
  const [topUpDialogOpen, setTopUpDialogOpen] = useState(false);
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [topUpKind, setTopUpKind] = useState<'purchase' | 'adjustment'>('purchase');
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpConcept, setTopUpConcept] = useState('');

  // Suspender (con confirmación) / activar
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  // Eliminar el partner (borrado FÍSICO). Con cartera hace falta force=true y
  // el diálogo exige escribir el nombre exacto del partner.
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');

  // Resetear contraseña del usuario del portal partner: confirmación (con
  // email opcional — sin él, el usuario del alta) + contraseña generada
  // mostrada UNA sola vez (mismo patrón que el alta en PartnersPage)
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetInfo, setResetInfo] = useState<{
    email: string;
    generated_password: string;
    // El backend intenta enviarla también al buzón del usuario del portal; si no
    // salió, se mantiene el texto de siempre: entrégala tú por un canal seguro.
    email_sent?: boolean;
  } | null>(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);

  // Statement mensual — por defecto el mes en curso
  const now = new Date();
  const [stmtYear, setStmtYear] = useState(now.getFullYear());
  const [stmtMonth, setStmtMonth] = useState(now.getMonth() + 1);
  const [stmtLoading, setStmtLoading] = useState(false);
  const [statement, setStatement] = useState<ResellerMonthlyStatement | null>(null);

  const loadPartner = useCallback(async () => {
    try {
      setError('');
      const data = await apiService.getReseller(resellerId);
      setPartner(data);
    } catch (err: any) {
      console.error('Failed to load partner:', err);
      setError(err.detail || t('partners.detailLoadError', 'No se pudo cargar el partner'));
    } finally {
      setIsLoading(false);
    }
  }, [resellerId, t]);

  useEffect(() => {
    if (!Number.isNaN(resellerId)) loadPartner();
  }, [resellerId, loadPartner]);

  // ── Editar condiciones ────────────────────────────────────────────────
  const editDiscountValid =
    editDiscount.trim() !== '' &&
    !Number.isNaN(Number(editDiscount)) &&
    Number(editDiscount) >= 0 &&
    Number(editDiscount) <= 100;
  const editMaxClientsValid = /^\d+$/.test(editMaxClients.trim()) && Number(editMaxClients) >= 1;
  const editMaxWholesaleValid =
    editMaxWholesale.trim() !== '' &&
    !Number.isNaN(Number(editMaxWholesale)) &&
    Number(editMaxWholesale) >= 0;
  // 0 vale: es «sin límite», igual que en el techo de bolsa mayorista.
  const editMaxEmployeesValid = /^\d+$/.test(editMaxEmployees.trim()) && Number(editMaxEmployees) >= 0;
  const editMaxStorageValid = /^\d+$/.test(editMaxStorage.trim()) && Number(editMaxStorage) >= 0;
  // Incluido: 0 SÍ significa «todo su disco se factura desde el primer GiB»
  // (no es «sin límite»), así que también es un valor legítimo.
  const editIncludedStorageValid =
    /^\d+$/.test(editIncludedStorage.trim()) && Number(editIncludedStorage) >= 0;
  const canSaveEdit =
    editName.trim() !== '' && editDiscountValid && editMaxClientsValid &&
    editMaxWholesaleValid && editMaxEmployeesValid && editMaxStorageValid &&
    editIncludedStorageValid;

  const openEditDialog = () => {
    if (!partner) return;
    setEditName(partner.name);
    setEditDiscount(formatPct(partner.wholesale_discount_pct));
    setEditMaxClients(String(partner.max_clients));
    setEditMaxWholesale(String(Number(partner.max_wholesale_credits) || 0));
    setEditMaxEmployees(String(Number(partner.max_total_employees) || 0));
    setEditMaxStorage(String(Number(partner.max_total_storage_gib) || 0));
    setEditIncludedStorage(String(Number(partner.included_storage_gib ?? 20)));
    setEditNotes(partner.notes || '');
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!canSaveEdit) return;
    try {
      setEditLoading(true);
      await apiService.updateReseller(resellerId, {
        name: editName.trim(),
        wholesale_discount_pct: Number(editDiscount),
        max_clients: Number(editMaxClients),
        max_wholesale_credits: Number(editMaxWholesale),
        max_total_employees: Number(editMaxEmployees),
        max_total_storage_gib: Number(editMaxStorage),
        included_storage_gib: Number(editIncludedStorage),
        notes: editNotes
      });
      toast.success(t('partners.updated', 'Condiciones actualizadas'));
      setEditDialogOpen(false);
      loadPartner();
    } catch (err: any) {
      console.error('Failed to update partner:', err);
      toast.error(err.detail || t('partners.updateError', 'No se pudieron actualizar las condiciones'));
    } finally {
      setEditLoading(false);
    }
  };

  // ── Recarga mayorista ─────────────────────────────────────────────────
  // purchase: importe > 0 (respeta el techo). adjustment: cualquier signo,
  // distinto de 0 — el contrato exige amount>0 siempre, así que se envía
  // |importe| en amount y el valor con signo en amount_signed.
  const topUpNum = Number(topUpAmount);
  const topUpValid =
    topUpAmount.trim() !== '' &&
    !Number.isNaN(topUpNum) &&
    (topUpKind === 'purchase' ? topUpNum > 0 : topUpNum !== 0);

  const handleTopUp = async () => {
    if (!topUpValid) return;
    try {
      setTopUpLoading(true);
      const payload: WholesaleTopUpRequest = topUpKind === 'purchase'
        ? { amount: topUpNum, kind: 'purchase' }
        : { amount: Math.abs(topUpNum), amount_signed: topUpNum, kind: 'adjustment' };
      if (topUpConcept.trim()) payload.concept = topUpConcept.trim();
      const result = await apiService.addResellerCredits(resellerId, payload);
      toast.success(
        t('partners.topUpDone', 'Bolsa mayorista actualizada. Saldo: {{balance}}', {
          balance: formatCredits(result.wholesale_balance)
        })
      );
      setTopUpDialogOpen(false);
      setTopUpAmount('');
      setTopUpConcept('');
      setTopUpKind('purchase');
      loadPartner();
    } catch (err: any) {
      console.error('Failed to top up wholesale credits:', err);
      toast.error(err.detail || t('partners.topUpError', 'No se pudo recargar la bolsa mayorista'));
    } finally {
      setTopUpLoading(false);
    }
  };

  // ── Suspender / activar ───────────────────────────────────────────────
  const handleSuspend = async () => {
    try {
      setStatusLoading(true);
      await apiService.suspendReseller(resellerId);
      toast.success(t('partners.suspended', 'Partner suspendido'));
      setSuspendDialogOpen(false);
      loadPartner();
    } catch (err: any) {
      console.error('Failed to suspend partner:', err);
      toast.error(err.detail || t('partners.suspendError', 'No se pudo suspender el partner'));
    } finally {
      setStatusLoading(false);
    }
  };

  const handleActivate = async () => {
    try {
      setStatusLoading(true);
      await apiService.activateReseller(resellerId);
      toast.success(t('partners.activated', 'Partner reactivado'));
      loadPartner();
    } catch (err: any) {
      console.error('Failed to activate partner:', err);
      toast.error(err.detail || t('partners.activateError', 'No se pudo reactivar el partner'));
    } finally {
      setStatusLoading(false);
    }
  };

  // ── Eliminar partner (borrado físico) ─────────────────────────────────
  const clientsCount = partner?.clients_count ?? 0;
  const deleteNeedsForce = clientsCount > 0;
  const deleteConfirmed =
    !deleteNeedsForce || deleteConfirmName.trim() === (partner?.name || '');

  const openDeleteDialog = () => {
    setDeleteConfirmName('');
    setDeleteDialogOpen(true);
  };

  const handleDeletePartner = async () => {
    if (!deleteConfirmed) return;
    try {
      setDeleteLoading(true);
      const result = await apiService.deleteReseller(resellerId, deleteNeedsForce);
      toast.success(
        result.unlinked_clients > 0
          ? t('partners.deletedWithClients',
              'Partner eliminado; {{count}} cliente(s) pasan a cuentas directas del marketplace',
              { count: result.unlinked_clients })
          : t('partners.deleted', 'Partner eliminado')
      );
      navigate('/partners');
    } catch (err: any) {
      console.error('Failed to delete partner:', err);
      toast.error(err.detail || t('partners.deleteError', 'No se pudo eliminar el partner'));
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Resetear contraseña del portal partner ────────────────────────────
  const resetEmailValid =
    resetEmail.trim() === '' || /\S+@\S+\.\S+/.test(resetEmail.trim());

  const openResetDialog = () => {
    setResetEmail('');
    setResetConfirmOpen(true);
  };

  // Desbloqueo de un usuario del portal partner: POST /resellers/{id}/unlock.
  // Recarga el detalle para pintar el estado real que devuelve el backend.
  const handleUnlockPortalUser = async (email?: string) => {
    try {
      await apiService.unlockResellerUser(resellerId, email);
      toast.success(t('partners.unlockDone', 'Acceso desbloqueado'));
      await loadPartner();
    } catch (err: any) {
      console.error('Failed to unlock partner portal user:', err);
      toast.error(err.detail || t('partners.unlockError', 'No se pudo desbloquear el acceso'));
    }
  };

  const handleResetPassword = async () => {
    if (!resetEmailValid) return;
    try {
      setResetLoading(true);
      const result = await apiService.resetResellerPassword(
        resellerId,
        resetEmail.trim() || undefined
      );
      setResetConfirmOpen(false);
      setResetInfo(result);
      setResetPasswordDialogOpen(true);
    } catch (err: any) {
      console.error('Failed to reset partner password:', err);
      toast.error(err.detail || t('partners.resetPasswordError', 'No se pudo resetear la contraseña'));
    } finally {
      setResetLoading(false);
    }
  };

  // ── Statement mensual ─────────────────────────────────────────────────
  const handleLoadStatement = async () => {
    try {
      setStmtLoading(true);
      const data = await apiService.getResellerStatement(resellerId, stmtYear, stmtMonth);
      setStatement(data);
    } catch (err: any) {
      console.error('Failed to load statement:', err);
      toast.error(err.detail || t('partners.statementError', 'No se pudo cargar el statement'));
    } finally {
      setStmtLoading(false);
    }
  };

  const entryKindLabel = (entry: WholesaleEntry) => {
    switch (entry.kind) {
      case 'purchase':
        return t('partners.entryKind.purchase', 'Compra');
      case 'allocation':
        return t('partners.entryKind.allocation', 'Asignación a cliente');
      case 'adjustment':
        return t('partners.entryKind.adjustment', 'Ajuste');
      // Cargo automático por ciclo del almacenamiento de TODA su cartera: es
      // suyo, no de sus clientes (a ellos no se les cobra el disco).
      case 'storage':
        return t('partners.entryKind.storage', 'Almacenamiento');
      default:
        return entry.kind;
    }
  };

  const monthNames = [
    t('partners.months.jan', 'Enero'), t('partners.months.feb', 'Febrero'),
    t('partners.months.mar', 'Marzo'), t('partners.months.apr', 'Abril'),
    t('partners.months.may', 'Mayo'), t('partners.months.jun', 'Junio'),
    t('partners.months.jul', 'Julio'), t('partners.months.aug', 'Agosto'),
    t('partners.months.sep', 'Septiembre'), t('partners.months.oct', 'Octubre'),
    t('partners.months.nov', 'Noviembre'), t('partners.months.dec', 'Diciembre')
  ];
  const yearOptions = [now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear()];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !partner) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/partners')} sx={{ mb: 2 }}>
          {t('common.back', 'Atrás')}
        </Button>
        <Alert severity="error">
          {error || t('partners.notFound', 'Partner no encontrado')}
        </Alert>
      </Box>
    );
  }

  const isActive = partner.status === 'active';
  const noWholesaleCap = Number(partner.max_wholesale_credits) === 0;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/partners')}>
            {t('common.back', 'Atrás')}
          </Button>
          <Typography variant="h4" component="h1">
            {partner.name}
          </Typography>
          <Chip
            label={isActive
              ? t('partners.status.active', 'Activo')
              : t('partners.status.suspended', 'Suspendido')}
            color={isActive ? 'success' : 'default'}
            size="small"
          />
        </Box>
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={() => loadPartner()}>
            {t('common.refresh', 'Actualizar')}
          </Button>
          <Button variant="outlined" size="small" startIcon={<Edit />} onClick={openEditDialog}>
            {t('partners.editConditions', 'Editar condiciones')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            startIcon={<LockReset />}
            onClick={openResetDialog}
          >
            {t('partners.resetPassword', 'Resetear contraseña')}
          </Button>
          {isActive ? (
            <Button
              variant="outlined"
              size="small"
              color="warning"
              startIcon={<Block />}
              onClick={() => setSuspendDialogOpen(true)}
            >
              {t('partners.suspend', 'Suspender')}
            </Button>
          ) : (
            <LoadingButton
              variant="outlined"
              size="small"
              color="success"
              startIcon={<PlayArrow />}
              loading={statusLoading}
              onClick={handleActivate}
            >
              {t('partners.activate', 'Reactivar')}
            </LoadingButton>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<AccountBalanceWallet />}
            onClick={() => setTopUpDialogOpen(true)}
          >
            {t('partners.topUp', 'Recarga mayorista')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<DeleteForever />}
            onClick={openDeleteDialog}
          >
            {t('partners.delete', 'Eliminar')}
          </Button>
        </Box>
      </Box>

      {/* Datos + bolsa mayorista */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Handshake color="primary" />
                <Typography variant="h6">{t('partners.partnerData', 'Datos del partner')}</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partners.fields.contactEmail', 'Email de contacto')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {partner.contact_email}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partners.fields.clients', 'Clientes')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {partner.clients_count !== null && partner.clients_count !== undefined
                      ? `${partner.clients_count} / ${partner.max_clients}`
                      : `— / ${partner.max_clients}`}
                  </Typography>
                </Grid>
                {/* Ocupación del cupo AGREGADO de empleados IA: lo que el admin
                    mira para decidir si amplía (es carga real del cluster). */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partners.fields.employees', 'Empleados IA')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {partner.employees_count === null || partner.employees_count === undefined
                      ? '—'
                      : Number(partner.max_total_employees) > 0
                        ? `${partner.employees_count} / ${partner.max_total_employees}`
                        : partner.employees_count}
                  </Typography>
                  {!(Number(partner.max_total_employees) > 0) && (
                    <Typography variant="caption" color="text.secondary">
                      {t('partners.noEmployeeCap', 'Sin límite de empleados en su cartera')}
                    </Typography>
                  )}
                </Grid>
                {/* Ocupación del techo AGREGADO de almacenamiento: GiB de los
                    volúmenes de todos los empleados de su cartera. */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partners.fields.storage', 'Almacenamiento')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {partner.storage_used_gib === null || partner.storage_used_gib === undefined
                      ? '—'
                      : Number(partner.max_total_storage_gib) > 0
                        ? `${partner.storage_used_gib} / ${partner.max_total_storage_gib} GiB`
                        : `${partner.storage_used_gib} GiB`}
                  </Typography>
                  {!(Number(partner.max_total_storage_gib) > 0) && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('partners.noStorageCap', 'Sin límite de almacenamiento en su cartera')}
                    </Typography>
                  )}
                  {/* FACTURACIÓN del disco: lo incluido, lo que excede y el
                      cargo por ciclo. Se le cobra AL PARTNER (sus clientes no
                      pagan almacenamiento), así que el admin ve aquí
                      exactamente los mismos números que él en su panel. */}
                  {partner.included_storage_gib !== null
                    && partner.included_storage_gib !== undefined && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('partners.storageIncluded', '{{gib}} GiB incluidos sin coste', {
                        gib: partner.included_storage_gib
                      })}
                    </Typography>
                  )}
                  {Number(partner.storage_billable_gib ?? 0) > 0 && (
                    <Typography variant="caption" color="warning.main" display="block">
                      {t('partners.storageBillable',
                        '{{gib}} GiB facturables · {{credits}} créditos/ciclo',
                        {
                          gib: partner.storage_billable_gib,
                          credits: formatCredits(partner.storage_cycle_credits ?? 0)
                        })}
                    </Typography>
                  )}
                  {Number(partner.storage_allocated_gib ?? 0) > 0
                    && Number(partner.max_total_storage_gib) > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('partners.storageAllocated', 'Reparte {{allocated}} de {{max}} GiB entre sus clientes', {
                        allocated: partner.storage_allocated_gib,
                        max: partner.max_total_storage_gib
                      })}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partners.fields.discount', 'Descuento')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatPct(partner.wholesale_discount_pct)} %
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partners.fields.createdAt', 'Creado')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {format(new Date(partner.created_at), 'dd/MM/yyyy HH:mm')}
                  </Typography>
                </Grid>
                {partner.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {t('partners.fields.notes', 'Notas')}
                    </Typography>
                    <Typography variant="body2">{partner.notes}</Typography>
                  </Grid>
                )}
                {/* Contrato F1: acceso al portal de cada usuario del partner —
                    por qué no puede entrar y el botón de desbloqueo (que NO
                    toca su contraseña, a diferencia del reset). */}
                {(partner.users_access || []).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {t('partners.fields.portalAccess', 'Acceso al portal')}
                    </Typography>
                    {(partner.users_access || []).map((access) => (
                      <Box key={access.email || 'user'} mt={1}>
                        <Typography variant="body2" fontWeight={600}>
                          {access.email || '—'}
                        </Typography>
                        <PortalAccessPanel
                          access={access}
                          onUnlock={() => handleUnlockPortalUser(access.email || undefined)}
                        />
                      </Box>
                    ))}
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AccountBalanceWallet color="primary" />
                <Typography variant="h6">{t('partners.wholesaleCardTitle', 'Bolsa mayorista')}</Typography>
              </Box>
              <Typography variant="h4" fontWeight={700}>
                {formatCredits(partner.wholesale_balance)}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('partners.creditsUnit', 'créditos')}
              </Typography>
              {/* El «techo» es un LÍMITE DE COMPRA MENSUAL: cuántos créditos
                  puede comprar por ciclo (no un tope de saldo). El admin ve lo
                  mismo que el partner: su límite y lo ya comprado este mes. */}
              <Typography variant="body2" color="text.secondary">
                {noWholesaleCap
                  ? t('partners.noWholesaleCap', 'Sin límite de compra mensual')
                  : t('partners.wholesaleCap', 'Límite de compra mensual: {{cap}} créditos', {
                      cap: formatCredits(partner.max_wholesale_credits)
                    })}
              </Typography>
              {!noWholesaleCap && (
                <Typography variant="body2" color="text.secondary">
                  {t('partners.wholesalePurchasedThisCycle', 'Este mes ha comprado {{purchased}} de {{cap}}{{renews}}', {
                    purchased: formatCredits(partner.wholesale_purchased_this_cycle ?? 0),
                    cap: formatCredits(partner.max_wholesale_credits),
                    renews: partner.wholesale_cycle_renews_at
                      ? ' · ' + t('partners.wholesaleRenews', 'se renueva el {{date}}', {
                          date: format(new Date(partner.wholesale_cycle_renews_at), 'dd/MM/yyyy')
                        })
                      : ''
                  })}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Ledger mayorista */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <AccountBalanceWallet color="primary" />
            <Typography variant="h6">{t('partners.wholesaleLedger', 'Movimientos de la bolsa mayorista')}</Typography>
          </Box>
          {partner.credit_entries.length === 0 ? (
            <Typography color="text.secondary" align="center" py={2}>
              {t('partners.noEntries', 'Sin movimientos todavía.')}
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.date', 'Fecha')}</TableCell>
                    <TableCell>{t('partners.fields.kind', 'Tipo')}</TableCell>
                    <TableCell>{t('partners.fields.concept', 'Concepto')}</TableCell>
                    <TableCell>{t('partners.fields.clientAccount', 'Cuenta de cliente')}</TableCell>
                    {/* La bolsa mayorista se mide en CRÉDITOS. El importe en dinero solo
                        existe en el statement mensual, que es donde se factura. */}
                    <TableCell align="right">{t('partners.fields.credits', 'Créditos')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {partner.credit_entries.map((entry) => {
                    const amount = Number(entry.amount);
                    return (
                      <TableRow key={entry.id} hover>
                        <TableCell>{format(new Date(entry.created_at), 'dd/MM/yyyy HH:mm')}</TableCell>
                        <TableCell>
                          <Chip
                            label={entryKindLabel(entry)}
                            size="small"
                            color={entry.kind === 'purchase'
                              ? 'success'
                              : entry.kind === 'allocation'
                                ? 'default'
                                : entry.kind === 'storage'
                                  ? 'warning'
                                  : 'info'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{entry.concept || '—'}</TableCell>
                        <TableCell>
                          {entry.client_account_id ? (
                            <Link
                              component="button"
                              type="button"
                              onClick={() => navigate(`/client-accounts/${entry.client_account_id}`)}
                            >
                              #{entry.client_account_id}
                            </Link>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={amount < 0 ? 'error.main' : 'success.main'}
                          >
                            {amount > 0 ? '+' : ''}{formatCredits(entry.amount)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Statement mensual */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <ReceiptLong color="primary" />
              <Typography variant="h6">{t('partners.statement', 'Statement mensual')}</Typography>
            </Box>
            <Box display="flex" gap={2} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>{t('partners.fields.month', 'Mes')}</InputLabel>
                <Select
                  value={stmtMonth}
                  label={t('partners.fields.month', 'Mes')}
                  onChange={(e) => setStmtMonth(Number(e.target.value))}
                >
                  {monthNames.map((name, idx) => (
                    <MenuItem key={idx + 1} value={idx + 1}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>{t('partners.fields.year', 'Año')}</InputLabel>
                <Select
                  value={stmtYear}
                  label={t('partners.fields.year', 'Año')}
                  onChange={(e) => setStmtYear(Number(e.target.value))}
                >
                  {yearOptions.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <LoadingButton
                variant="outlined"
                size="small"
                loading={stmtLoading}
                onClick={handleLoadStatement}
              >
                {t('partners.loadStatement', 'Ver statement')}
              </LoadingButton>
            </Box>
          </Box>

          {statement === null ? (
            <Typography color="text.secondary" align="center" py={2}>
              {t('partners.statementHint', 'Elige mes y año y pulsa «Ver statement» para derivar la factura del ledger.')}
            </Typography>
          ) : (
            <Box>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partners.stmt.purchases', 'Compras del mes')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatCredits(statement.purchases_total)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partners.stmt.allocated', 'Asignado a clientes')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatCredits(statement.allocated_total)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partners.stmt.adjustments', 'Ajustes')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatCredits(statement.adjustments_total)}
                  </Typography>
                </Grid>
                {/* Cargo del ALMACENAMIENTO de su cartera en el mes: créditos
                    de su bolsa, NO importe en USD — por eso no entra en «a
                    pagar» ni lleva descuento (el descuento se aplicó al comprar
                    esos créditos; gastarlos vale igual para todos). */}
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partners.stmt.storage', 'Almacenamiento')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatCredits(statement.storage_total ?? 0)}
                  </Typography>
                  {Number(statement.storage_billable_gib ?? 0) > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {t('partners.stmt.storageGib', '{{gib}} GiB sobre {{included}} incluidos', {
                        gib: statement.storage_billable_gib,
                        included: statement.storage_included_gib ?? 0
                      })}
                    </Typography>
                  )}
                </Grid>
                {/* Devoluciones de plan no consumido acreditadas en el mes:
                    créditos que VUELVEN a la bolsa del partner (el remanente que
                    sus clientes no gastaron al cerrar su ciclo). Como el
                    almacenamiento, es créditos y no importe — no resta de «a
                    pagar». Solo se pinta si hubo alguna. */}
                {Number(statement.plan_refunds_total ?? 0) > 0 && (
                  <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="body2" color="text.secondary">
                      {t('partners.stmt.planRefunds', 'Devoluciones de plan')}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatCredits(statement.plan_refunds_total ?? 0)}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partners.stmt.discount', 'Descuento')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatPct(statement.wholesale_discount_pct)} %
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partners.stmt.gross', 'Importe lista (USD)')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatCredits(statement.gross_amount_usd)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('partners.stmt.due', 'A pagar (USD)')}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="primary.main">
                    {formatCredits(statement.amount_due_usd)}
                  </Typography>
                </Grid>
              </Grid>
              {statement.clients.length === 0 ? (
                <Typography color="text.secondary" align="center" py={2}>
                  {t('partners.stmt.noClients', 'Sin asignaciones a clientes en este periodo.')}
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('partners.stmt.client', 'Cliente')}</TableCell>
                        <TableCell align="right">{t('partners.stmt.clientAllocated', 'Créditos asignados')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {statement.clients.map((line) => (
                        <TableRow key={line.client_account_id} hover>
                          <TableCell>
                            <Link
                              component="button"
                              type="button"
                              onClick={() => navigate(`/client-accounts/${line.client_account_id}`)}
                            >
                              {line.client_name || `#${line.client_account_id}`}
                            </Link>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {formatCredits(line.allocated)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Diálogo editar condiciones — PATCH /resellers/{id} */}
      <Dialog open={editDialogOpen} onClose={() => !editLoading && setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('partners.editConditions', 'Editar condiciones')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('partners.fields.name', 'Nombre') + ' *'}
            fullWidth
            variant="outlined"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('partners.fields.discount', 'Descuento') + ' (%)'}
            type="number"
            fullWidth
            variant="outlined"
            value={editDiscount}
            onChange={(e) => setEditDiscount(e.target.value)}
            inputProps={{ min: 0, max: 100, step: '0.01' }}
            error={!editDiscountValid}
            helperText={!editDiscountValid
              ? t('partners.discountInvalid', 'Debe ser un porcentaje entre 0 y 100')
              : t('partners.fields.discountHelp', 'Descuento mayorista sobre el precio de lista de los créditos')}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('partners.fields.maxClients', 'Máx. clientes')}
            type="number"
            fullWidth
            variant="outlined"
            value={editMaxClients}
            onChange={(e) => setEditMaxClients(e.target.value)}
            inputProps={{ min: 1, step: 1 }}
            error={!editMaxClientsValid}
            helperText={!editMaxClientsValid
              ? t('partners.maxClientsInvalid', 'Debe ser un entero mayor o igual que 1')
              : t('partners.maxClientsPatchHelp', 'Bajarlo por debajo de la cartera actual solo frena altas futuras, nunca desmonta clientes')}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('partners.fields.maxWholesale', 'Límite de compra mensual (créditos)')}
            type="number"
            fullWidth
            variant="outlined"
            value={editMaxWholesale}
            onChange={(e) => setEditMaxWholesale(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            error={!editMaxWholesaleValid}
            helperText={!editMaxWholesaleValid
              ? t('partners.maxWholesaleInvalid', 'Debe ser un número mayor o igual que 0')
              : t('partners.fields.maxWholesaleHelp', 'Cuántos créditos puede COMPRAR el partner por mes (no un tope de saldo: asignar a clientes no libera cupo). 0 = sin límite. Se renueva cada mes desde su primera compra')}
            sx={{ mb: 2 }}
          />
          {/* Cupo AGREGADO de empleados IA — el que gobierna la carga del
              cluster. Bajarlo por debajo de lo ya desplegado NO desmonta nada. */}
          <TextField
            margin="dense"
            label={t('partners.fields.maxTotalEmployees', 'Máx. empleados (todos sus clientes)')}
            type="number"
            fullWidth
            variant="outlined"
            value={editMaxEmployees}
            onChange={(e) => setEditMaxEmployees(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            error={!editMaxEmployeesValid}
            helperText={!editMaxEmployeesValid
              ? t('partners.maxTotalEmployeesInvalid', 'Debe ser un entero mayor o igual que 0')
              : t('partners.maxTotalEmployeesPatchHelp',
                  'Techo de empleados IA vivos en TODA su cartera: gobierna la carga del cluster (cada empleado es una aplicación desplegada). 0 = sin límite. Bajarlo solo frena contrataciones futuras, nunca retira empleados ya desplegados')}
            sx={{ mb: 2 }}
          />
          {/* Techo AGREGADO de ALMACENAMIENTO. Mismo criterio: bajarlo no
              encoge ningún disco ya creado, solo frena ampliaciones. */}
          <TextField
            margin="dense"
            label={t('partners.fields.maxTotalStorage', 'Máx. almacenamiento (GiB, todos sus clientes)')}
            type="number"
            fullWidth
            variant="outlined"
            value={editMaxStorage}
            onChange={(e) => setEditMaxStorage(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            error={!editMaxStorageValid}
            helperText={!editMaxStorageValid
              ? t('partners.maxTotalStorageInvalid', 'Debe ser un entero mayor o igual que 0')
              : t('partners.maxTotalStoragePatchHelp',
                  'Techo de GiB sumando los volúmenes de TODOS los empleados de su cartera. 0 = sin límite. Bajarlo solo frena ampliaciones y contrataciones futuras: ningún disco ya creado se encoge')}
            sx={{ mb: 2 }}
          />
          {/* Almacenamiento INCLUIDO del partner: lo que el marketplace le
              regala. El exceso se le cobra A ÉL por ciclo — sus clientes no
              pagan almacenamiento — y el cambio se nota en el próximo cargo,
              nunca en los ya asentados. */}
          <TextField
            margin="dense"
            label={t('partners.fields.includedStorage', 'Almacenamiento incluido (GiB)')}
            type="number"
            fullWidth
            variant="outlined"
            value={editIncludedStorage}
            onChange={(e) => setEditIncludedStorage(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            error={!editIncludedStorageValid}
            helperText={!editIncludedStorageValid
              ? t('partners.includedStorageInvalid', 'Debe ser un entero mayor o igual que 0')
              : t('partners.includedStoragePatchHelp',
                  'GiB sin coste sobre el TOTAL de su cartera; lo que pase de ahí se le cobra a él por ciclo (sus clientes no pagan almacenamiento). 0 = se factura desde el primer GiB. El cambio aplica al próximo cargo, no a los ya asentados')}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('partners.fields.notes', 'Notas') + ' (' + t('common.optional', 'Opcional') + ')'}
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={editLoading}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton
            onClick={handleSaveEdit}
            loading={editLoading}
            disabled={!canSaveEdit}
            variant="contained"
          >
            {t('common.save', 'Guardar')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Diálogo recarga mayorista — POST /resellers/{id}/credits */}
      <Dialog open={topUpDialogOpen} onClose={() => !topUpLoading && setTopUpDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('partners.topUp', 'Recarga mayorista')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth variant="outlined" margin="dense" sx={{ mb: 2, mt: 1 }}>
            <InputLabel>{t('partners.fields.kind', 'Tipo')}</InputLabel>
            <Select
              value={topUpKind}
              label={t('partners.fields.kind', 'Tipo')}
              onChange={(e) => setTopUpKind(e.target.value as 'purchase' | 'adjustment')}
            >
              <MenuItem value="purchase">{t('partners.entryKind.purchase', 'Compra')}</MenuItem>
              <MenuItem value="adjustment">{t('partners.entryKind.adjustment', 'Ajuste')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            autoFocus
            margin="dense"
            label={t('partners.fields.credits', 'Créditos') + ' *'}
            type="number"
            fullWidth
            variant="outlined"
            value={topUpAmount}
            onChange={(e) => setTopUpAmount(e.target.value)}
            inputProps={topUpKind === 'purchase' ? { min: 0, step: '0.01' } : { step: '0.01' }}
            error={topUpAmount !== '' && !topUpValid}
            helperText={topUpKind === 'purchase'
              ? t('partners.topUpPurchaseHelp', 'Compra mayorista: importe positivo, respeta el techo de bolsa')
              : t('partners.topUpAdjustmentHelp', 'Corrección del admin: admite signo negativo y se salta el techo')}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label={t('partners.fields.concept', 'Concepto') + ' (' + t('common.optional', 'Opcional') + ')'}
            fullWidth
            variant="outlined"
            value={topUpConcept}
            onChange={(e) => setTopUpConcept(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTopUpDialogOpen(false)} disabled={topUpLoading}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton
            onClick={handleTopUp}
            loading={topUpLoading}
            disabled={!topUpValid}
            variant="contained"
          >
            {t('partners.topUpConfirm', 'Aplicar')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Confirmación de suspensión — corta TODO el portal partner */}
      <Dialog open={suspendDialogOpen} onClose={() => !statusLoading && setSuspendDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('partners.suspendTitle', '¿Suspender este partner?')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            {t('partners.suspendWarning', 'Se corta todo el portal del partner (incluido el login). Sus clientes finales siguen operando con normalidad.')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendDialogOpen(false)} disabled={statusLoading}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton
            onClick={handleSuspend}
            loading={statusLoading}
            variant="contained"
            color="warning"
          >
            {t('partners.suspend', 'Suspender')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Eliminar partner — DELETE /resellers/{id}?force=... (borrado FÍSICO) */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleteLoading && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('partners.deleteTitle', '¿Eliminar este partner?')}</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>{t('partners.deleteIrreversibleTitle', 'Esta acción es IRREVERSIBLE')}</AlertTitle>
            {t(
              'partners.deleteConsequences',
              'Se borra físicamente el partner: sus usuarios de portal, su historial de impersonaciones y TODO su ledger mayorista (compras, asignaciones y ajustes). No hay papelera ni recuperación posible.'
            )}
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: deleteNeedsForce ? 2 : 0 }}>
            {t(
              'partners.deleteClientsNote',
              'Sus clientes NO se tocan: pasan a ser cuentas directas del marketplace y siguen operando con su saldo y sus empleados intactos.'
            )}
          </Typography>
          {deleteNeedsForce && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t(
                  'partners.deleteForceWarning',
                  'El partner tiene {{count}} cliente(s) en cartera: para confirmar el borrado forzado escribe el nombre exacto del partner.',
                  { count: clientsCount }
                )}
              </Alert>
              <TextField
                autoFocus
                margin="dense"
                label={t('partners.deleteConfirmLabel', 'Escribe «{{name}}» para confirmar', { name: partner.name })}
                fullWidth
                variant="outlined"
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                error={deleteConfirmName !== '' && !deleteConfirmed}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton
            onClick={handleDeletePartner}
            loading={deleteLoading}
            disabled={!deleteConfirmed}
            variant="contained"
            color="error"
          >
            {t('partners.deleteConfirm', 'Eliminar definitivamente')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Confirmación de reset — POST /resellers/{id}/reset-password */}
      <Dialog
        open={resetConfirmOpen}
        onClose={() => !resetLoading && setResetConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('partners.resetPasswordTitle', '¿Resetear la contraseña del portal partner?')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t(
              'partners.resetPasswordWarning',
              'Se generará una contraseña nueva y la actual dejará de valer inmediatamente. La nueva se mostrará UNA sola vez.'
            )}
          </Alert>
          <TextField
            margin="dense"
            label={t('partners.fields.resetEmail', 'Email del usuario') + ' (' + t('common.optional', 'Opcional') + ')'}
            type="email"
            fullWidth
            variant="outlined"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            error={!resetEmailValid}
            helperText={!resetEmailValid
              ? t('partners.resetEmailInvalid', 'Debe ser un email válido')
              : t('partners.resetEmailHelp', 'Déjalo vacío para resetear el usuario principal del portal (el creado en el alta)')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetConfirmOpen(false)} disabled={resetLoading}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <LoadingButton
            onClick={handleResetPassword}
            loading={resetLoading}
            disabled={!resetEmailValid}
            variant="contained"
            color="warning"
          >
            {t('partners.resetPassword', 'Resetear contraseña')}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Contraseña reseteada — se muestra UNA sola vez (mismo patrón que el alta) */}
      <Dialog
        open={resetPasswordDialogOpen}
        onClose={() => setResetPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle color="success" />
            {t('partners.resetPasswordDoneTitle', 'Contraseña reseteada')}
          </Box>
        </DialogTitle>
        <DialogContent>
          {resetInfo && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>{t('partners.passwordOnceTitle', 'Importante: guarda esta contraseña')}</AlertTitle>
                {t('partners.passwordOnceMessage', 'La contraseña no volverá a mostrarse. Cópiala y entrégasela al partner por un canal seguro.')}
                {/* Solo si el correo salió de verdad (envío fail-soft en backend). */}
                {resetInfo.email_sent && (
                  <Box sx={{ mt: 1 }}>
                    {t('partners.passwordAlsoEmailed', 'También se ha enviado por correo a {{email}}', {
                      email: resetInfo.email
                    })}
                  </Box>
                )}
              </Alert>
              <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('partners.fields.userEmail', 'Email del usuario')}
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', mb: 2 }}>
                  {resetInfo.email}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('partners.fields.generatedPassword', 'Contraseña generada')}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {resetInfo.generated_password}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(resetInfo.generated_password);
                      toast.success(t('partners.passwordCopied', 'Contraseña copiada al portapapeles'));
                    }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialogOpen(false)} variant="contained">
            {t('partners.passwordSaved', 'He guardado la contraseña')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerDetailAdminPage;
